import type {
    Dispatch,
    PropsWithChildren,
    ReactElement,
    SetStateAction,
} from 'react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import type {
    GetManufactureDetailsQuery,
    GetManufactureDetailsQueryVariables,
    ManufactureDetail,
} from '../gql';

import { useQuery } from '@apollo/client/react';

import { GetManufactureDetailsDocument } from '../gql';
import { GraphClientNames } from '../types/GQLClientNames';
import { isDefined } from '../utils/filter-util';

interface ManufactureDetailsContextType {
    manufactureDetails: Array<ManufactureDetail>;
    isLoading: boolean;
    generalQuery: string;
    setGeneralQuery: Dispatch<SetStateAction<string>>;
    fetchMore: () => void;
}

const ManufactureDetailsContext =
    createContext<ManufactureDetailsContextType | null>(null);

const useManufactureDetails = (): ManufactureDetailsContextType => {
    const context = useContext(ManufactureDetailsContext);
    if (!context)
        throw new Error(
            'useManufactureDetails must be used within a ManufactureDetailsProvider'
        );
    return context;
};

interface ManufactureDetailsProviderProps {
    materialSubcategoryPublicId: string | undefined;
    skip: boolean;
    onError: (error: Error, code: string) => void;
}

const PAGE_SIZE = 100;

const ExtendedManufactureDetailsProvider = ({
    materialSubcategoryPublicId,
    skip,
    children,
    onError: handleError,
}: PropsWithChildren<ManufactureDetailsProviderProps>): ReactElement => {
    const [generalQuery, setGeneralQuery] = useState('');

    const { data, error, loading, fetchMore } = useQuery<
        GetManufactureDetailsQuery,
        GetManufactureDetailsQueryVariables
    >(GetManufactureDetailsDocument, {
        variables: {
            materialSubcategoryPublicId: materialSubcategoryPublicId ?? '',
            generalQuery,
            isDeleted: false,
            pagination: {
                first: PAGE_SIZE,
            },
        },
        skip: skip || !materialSubcategoryPublicId,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        context: {
            clientName:
                GraphClientNames['bestone-bi4-buying-ext-composition-bff'],
        },
    });

    const manufactureDetails = (
        data?.manufactureDetails?.edges?.map((edge) => edge?.node) || []
    ).filter(isDefined);

    const fetchMoreManufactureDetails = useCallback(() => {
        if (data?.manufactureDetails?.pageInfo?.hasNextPage) {
            void fetchMore({
                variables: {
                    pagination: {
                        first: PAGE_SIZE,
                        after: data.manufactureDetails.pageInfo.endCursor,
                    },
                    generalQuery,
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
                    manufactureDetails: {
                        totalCount:
                            previousQueryResult.manufactureDetails
                                ?.totalCount ?? 0,
                        edges: [
                            ...(previousQueryResult.manufactureDetails?.edges ??
                                []),
                            ...(fetchMoreResult.manufactureDetails?.edges ??
                                []),
                        ],
                        pageInfo: fetchMoreResult.manufactureDetails?.pageInfo,
                    },
                }),
            });
        }
    }, [fetchMore, generalQuery, data?.manufactureDetails?.pageInfo]);

    const contextValue = useMemo(
        (): ManufactureDetailsContextType => ({
            manufactureDetails,
            isLoading: loading,
            fetchMore: fetchMoreManufactureDetails,
            generalQuery,
            setGeneralQuery,
        }),
        [manufactureDetails, loading, generalQuery, fetchMoreManufactureDetails]
    );

    useEffect(() => {
        if (error) {
            handleError(error, 'queryManufactureDetailsFailed');
        }
    }, [error, handleError]);

    return (
        <ManufactureDetailsContext.Provider value={contextValue}>
            {children}
        </ManufactureDetailsContext.Provider>
    );
};

export default ExtendedManufactureDetailsProvider;
export { useManufactureDetails, ManufactureDetailsContext };
export type { ManufactureDetailsProviderProps };
